// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { OffsetOptions } from "@floating-ui/dom";

import { useId } from "@floating-ui/react";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import FancyScroll from "@synd/App/Components/Common/FancyScroll";
import FloutingUiMenu, { MenuItem } from "@synd/App/Components/Menu/DropdownMenu";
import LazyImage from "@synd/MobiusOverrides/LazyImage";
import { NavigationContent } from "@synd/Overrides/Widgets/Header/MainNavigation";
import { xParserOptions } from "@synd/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC, memo, useCallback, useRef } from "react";
import styled, { css, ThemeProps, useTheme } from "styled-components";

export type MenuVariants = "primary" | "secondary";

export const DEFAULT_BOUNDARY_SELECTOR = "header [data-test-selector='main-navigation'] .container";

export interface MappedLink {
    // expanded from content-library/src/widgets/header/MainNavigation.tsx
    title: string;
    url?: string;
    type?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    // XNG-Changes: add "AuthoredMenu"
    childrenType?: "MegaMenu" | "CascadingMenu" | "CustomLinkList" | "AuthoredMenu";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
    iconProps?: IconPresentationProps;
    content?: NavigationContent;
}

export interface MenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
    // XNG-Changes
    megaMenuItemParentLink?: LinkPresentationProps;
    megaMenuItemLinksWrapper?: InjectableCss;
    megaMenuItemLink?: LinkPresentationProps;
    megaMenuContentSection?: InjectableCss;
    megaMenuContentLink?: LinkPresentationProps;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        // XNG-Changes: update type
        menuTrigger: React.ReactElement<HTMLElement & { "data-open"?: string }>;
        /**
         * @deprecated Use CSS style to control width.
         * * Width of the menu and submenus in pixels.
         */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        /**
         * XNG-Changes
         * Choose 'Primary' when the subMenu needs to match the parent menu height.
         */
        menuVariant?: MenuVariants;
        /**
         * menu item index
         */
        referenceIndex?: number;
        offset?: OffsetOptions;
        isMegaMenu?: boolean;
        navContent?: NavigationContent;
        numberOfColumns?: number;
    }
>;

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
// XNG-Change remove theme from type
export type MenuProps = MenuComponentProps & MenuPresentationProps;

type MenuStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        /**
         * XNG-Changes
         */
        referenceIndex?: number;
        currentDepth?: number;
        isMegaMenu?: boolean;
        isAuthoredMenu?: boolean;
        numberOfColumns?: number;
    };

const MenuStyle = styled.div<MenuStyleProps>`
    background: ${getColor("common.background")};
    z-index: ${getProp("theme.zIndex.menu")};
    box-shadow: ${getProp("theme.shadows.2")};
    width: ${getProp("width", 175)}px;
    list-style: none;
    position: absolute;
    margin: 0;
    padding: 0;
    display: none;
    ${injectCss}
`;

// type MenuWrapperProps = { canBeOpen: boolean; isOpen?: true } & InjectableCss;

// const MenuWrapper = styled.nav<MenuWrapperProps>`
//     position: relative; /* stylelint-disable */
//     ${({ isOpen }) =>
//         isOpen &&
//         `& > ${MenuStyle} {
//                 display: block;
//             }
//     `}
//     ${({ canBeOpen }) =>
//         canBeOpen &&
//         `
//     &:focus-within > ${MenuStyle}  /* stylelint-enable */ {
//         display: block;
//     }
//     .trigger:focus + ${MenuStyle},
//     &:hover > ${MenuStyle},
//     &.focus-within > ${MenuStyle} {
//         display: block;
//     }`}
//     ${injectCss}
// `;

type MenuItemStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        onClick?: () => void;
    };

// XNG-Change
const MenuItemStyle = styled.div<MenuItemStyleProps>`
    position: relative;
    // &:focus-within > ${MenuStyle} {
    //     display: block;
    //     left: ${getProp("width", 175)}px;
    //     top: 0;
    // }
    // &:focus > ${MenuStyle}, &:hover > ${MenuStyle}, &.focus-within > ${MenuStyle} {
    //     display: block;
    //     left: ${getProp("width", 175)}px;
    //     top: 0;
    // }
    a {
        width: 100%;
        min-height: 42px;
        justify-content: space-between;
        &:hover {
            background: ${getColor("common.border")};
        }
    }
    ${injectCss}
`;
// XNG-Change
const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean }>`
    padding: 0;
    color: ${getColor("text.main")};
    display: block;
    box-sizing: border-box;
    ${({ hasChildren }: { hasChildren?: boolean }) => hasChildren && "width: calc(100% - 24px);"}
`;

// XNG-Changes
export const MegaMenuItem = styled.div`
    display: flex;
    row-gap: 8px;
    flex-direction: column;
    align-items: flex-start;
`;



const RenderMenuItems: FC<{
    childMenuItems: MappedLink[];
    currentDepth: number;
    maxDepth: number;
    width?: number;
    applyProp: any;
    applyStyledProp: any;
    resolvedMergeCss: boolean | undefined;
    displayChangeCustomerLink?: boolean;
    renderMenuItems: (
        items: MappedLink[],
        depth: number,
        isMegaMenu?: boolean,
        navContent?: NavigationContent,
    ) => React.ReactNode;
    menuVariant?: MenuVariants;
    isMegaMenu: boolean;
}> = memo(
    ({
        childMenuItems,
        currentDepth,
        maxDepth,
        width,
        applyProp,
        applyStyledProp,
        resolvedMergeCss = true,
        displayChangeCustomerLink,
        renderMenuItems,
        menuVariant,
        isMegaMenu,
    }) => {
        const id = useId();
        return (
            <React.Fragment key={id}>
                {childMenuItems
                    ?.filter(item => !item.excludeFromNavigation)
                    .map((item, index, filteredItems) => {
                        const hasChildren =
                            item.children?.some(o => !o.excludeFromNavigation) && currentDepth < maxDepth;

                        if (item.type === "ChangeCustomerPage" && !displayChangeCustomerLink) {
                            return null;
                        }

                        // XNG-Changes : set L2 color to black in case of L2 have no L3                       
                        const noL2HasL3 =
                            isMegaMenu &&
                            !filteredItems.some(sibling => sibling.children && sibling.children.length > 0);

                        const itemElement = isMegaMenu ? (
                            <MegaMenuItem>
                                {item.url && (
                                    <Link
                                        key={`${item.title}_${index}`}
                                        href={item.url}
                                        {...applyProp("megaMenuItemParentLink")}
                                        // XNG-Changes
                                        {...(noL2HasL3 && { color: "black" })}
                                    >
                                        {item.title}
                                    </Link>
                                )}

                                {hasChildren && (
                                    <>
                                        <StyledWrapper {...applyProp("megaMenuItemLinksWrapper")}>
                                            {item.children?.map((item, index) => (
                                                <Link
                                                    key={`${item.title}_${index}`}
                                                    href={item.url}
                                                    {...applyProp("megaMenuItemLink")}
                                                >
                                                    {item.title}
                                                </Link>
                                            ))}
                                        </StyledWrapper>
                                    </>
                                )}
                            </MegaMenuItem>
                        ) : (
                            <MenuItem
                                targetComponent={
                                    <MenuItemStyle
                                        width={width}
                                        css={applyStyledProp(["cssOverrides", "menuItem"], resolvedMergeCss)}
                                        key={`${item.title}_${index}`}
                                        data-test-selector="menuItem"
                                    >
                                        <Clickable
                                            href={item.url}
                                            {...applyProp("menuItemClickableProps")}
                                            {...item.clickableProps}
                                        >
                                            {item.iconProps && <IconMemo {...item.iconProps} />}
                                            <MenuItemText
                                                hasChildren={hasChildren}
                                                {...applyProp("menuItemTypographyProps")}
                                                data-test-selector="menuItemText"
                                            >
                                                {item.title}
                                            </MenuItemText>
                                            {hasChildren && <IconMemo {...applyProp("moreIconProps")} />}
                                        </Clickable>
                                    </MenuItemStyle>
                                }
                            />
                        );

                        if (hasChildren && !isMegaMenu) {
                            return (
                                <FloutingUiMenu
                                    key={`FloutingUiMenu_${item.title}_${index}`}
                                    menuTrigger={itemElement}
                                    offset={0}
                                    placement="right-start"
                                    shift={{
                                        boundary:
                                            typeof document !== "undefined"
                                                ? document.querySelector(DEFAULT_BOUNDARY_SELECTOR) || document.body
                                                : "clippingAncestors",
                                        padding: 16,
                                    }}
                                    variant={menuVariant}
                                    delay={{ open: 200, close: 350 }}
                                >
                                    {renderMenuItems(item!.children!, currentDepth + 1, isMegaMenu)}
                                </FloutingUiMenu>
                            );
                        }

                        return <React.Fragment key={`MenuItem_${item.title}_${id}`}>{itemElement}</React.Fragment>;
                    })}
            </React.Fragment>
        );
    },
);


const Menu: FC<MenuProps> = ({
    menuItems,
    menuTrigger,
    isOpen,
    mergeCss,
    menuVariant,
    referenceIndex,
    offset,
    id,
    ...otherProps
}) => {
    const menuStyleRef = useRef<HTMLDivElement>(null);
    const theme = useTheme() as BaseTheme;
    const { applyProp, applyStyledProp } = applyPropBuilder({ ...otherProps, theme }, { component: "menu" });
    const resolvedMergeCss = mergeCss ?? theme?.menu?.defaultProps?.mergeCss;
    // XNG-Changes     
    const { maxDepth = 3, width, displayChangeCustomerLink, isMegaMenu, navContent, numberOfColumns } = otherProps;

    const renderMenuItems = useCallback(
        (childMenuItems: MappedLink[], currentDepth: number, isMegaMenu = false, navContent?: NavigationContent) => (
            <StyledWrapper
                css={css`
                    display: flex;
                    flex-wrap: nowrap;
                    height: 100%;
                `}
            >
                <MenuStyle
                    ref={menuStyleRef}
                    referenceIndex={referenceIndex}
                    css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                    isMegaMenu={isMegaMenu}
                    currentDepth={currentDepth}
                    // XNG-Changes                     
                    numberOfColumns={numberOfColumns}
                >
                    <FancyScroll
                        refElement={menuStyleRef}
                        extendedStyles={{
                            css: css`
                                height: 100%;
                                line-height: 0;
                            `,
                        }}
                    >
                        <RenderMenuItems
                            childMenuItems={childMenuItems}
                            currentDepth={currentDepth}
                            width={width}
                            maxDepth={maxDepth}
                            applyProp={applyProp}
                            applyStyledProp={applyStyledProp}
                            resolvedMergeCss={resolvedMergeCss}
                            displayChangeCustomerLink={displayChangeCustomerLink}
                            renderMenuItems={renderMenuItems}
                            menuVariant={menuVariant}
                            isMegaMenu={isMegaMenu}
                        />
                    </FancyScroll>
                </MenuStyle>
                {/* XNG-Changes */}
                {isMegaMenu &&
                    navContent &&
                    (navContent.htmlContent ? (
                        <StyledWrapper {...applyProp("megaMenuContentSection")}>
                            {parse(navContent.htmlContent, xParserOptions)}
                        </StyledWrapper>
                    ) : (
                        (!!navContent.title || !!navContent.image) && (
                            <StyledWrapper {...applyProp("megaMenuContentSection")}>
                                {navContent.title && <Typography variant="h4">{navContent.title}</Typography>}
                                {navContent.subtitle && <Typography>{navContent.subtitle}</Typography>}
                                {navContent.link && (
                                    <Link href={navContent.link} {...applyProp("megaMenuContentLink")}>
                                        {navContent.linkText}
                                    </Link>
                                )}
                                {navContent.image && (
                                    <LazyImage
                                        src={navContent.image}
                                        altText={navContent.title}
                                        width="100%"
                                        height="292px"
                                    />
                                )}
                            </StyledWrapper>
                        )
                    ))}
            </StyledWrapper>
        ),
        [
            applyStyledProp,
            resolvedMergeCss,
            width,
            displayChangeCustomerLink,
            maxDepth,
            isMegaMenu,
            navContent,
            // XNG-Changes             
            numberOfColumns,
        ],
    );

    return (
        <FloutingUiMenu
            menuTrigger={menuTrigger}
            overlay={true}
            offset={offset || { mainAxis: 0 }}
            placement="bottom-start"
            shift={{
                boundary:
                    typeof document !== "undefined"
                        ? document.querySelector(DEFAULT_BOUNDARY_SELECTOR) || document.body
                        : "clippingAncestors",
                padding: 16,
            }}
            variant={menuVariant}
            delay={{ open: 200, close: 300 }}
        >
            {renderMenuItems(menuItems, 1, isMegaMenu, navContent)}
        </FloutingUiMenu>
    );
};

/** @component */
export default Menu;

export { MenuItemText };




// const trigger = React.createRef<HTMLElement>();

// interface MenuState {
//     canBeOpen: boolean;
//     isMobile: boolean;
//     shouldRender: boolean;
// }

/**
 * A cascading menu that renders a defined number of sub-menus.
 */
// class Menu extends React.Component<MenuProps, MenuState> {
//     menuWrapperRef = React.createRef<HTMLElement>();

//     static defaultProps: Partial<MenuProps> = {
//         maxDepth: 3,
//         width: 175,
//     };

//     state: MenuState = {
//         canBeOpen: true,
//         isMobile: false,
//         shouldRender: true,
//     };

//     componentDidMount() {
//         this.setState({
//             isMobile: isMobile().any,
//         });

//         window.addEventListener("resize", () => {
//             window.setTimeout(() => {
//                 this.setState({
//                     isMobile: isMobile().any,
//                 });
//             }, 50);
//         });
//     }

//     componentWillUnmount() {
//         window.removeEventListener("resize", () => {
//             window.setTimeout(() => {
//                 this.setState({
//                     isMobile: isMobile().any,
//                 });
//             }, 50);
//         });
//     }

//     closeMenu = () => {
//         // On Mobile, the below logic
//         // where we switch the canBeOpen state from false to true
//         // causes the mobile experience to not function well
//         // due to Mobile not doing hover functionality

//         // Solution:
//         // I prevent the boolean toggle when we detect mobile

//         this.setState({ canBeOpen: this.state.isMobile }, () =>
//             setTimeout(() => {
//                 // The make the all browsers act the same on item click.
//                 if (document.activeElement instanceof HTMLElement) {
//                     document.activeElement.blur();
//                 }
//                 this.setState({ canBeOpen: true });
//             }, 100),
//         );
//     };

//     closeMenuWithoutChildren = (hasMenuChildren?: boolean) => {
//         // This is for high resolution mobile users.
//         // So that the menu closes on nav items without children
//         if (!hasMenuChildren) {
//             this.setState(
//                 {
//                     shouldRender: false,
//                 },
//                 () => {
//                     this.setState({ shouldRender: true });
//                 },
//             );
//         }
//     };

//     menuWrapperMouseLeaveHandler = () => {
//         if (!this.menuWrapperRef.current) {
//             return;
//         }

//         this.menuWrapperRef.current.blur();

//         const menuTrigger = this.menuWrapperRef.current.children[0] as HTMLElement;
//         if (!menuTrigger) {
//             return;
//         }

//         menuTrigger.blur();
//     };

//     render() {
//         return (
//             <ThemeConsumer>
//                 {theme => {
//                     const { menuItems, menuTrigger, isOpen, mergeCss, ...otherProps } = this.props;
//                     const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(
//                         { ...otherProps, theme },
//                         { component: "menu" },
//                     );
//                     const resolvedMergeCss = mergeCss ?? otherProps?.theme?.menu?.defaultProps?.mergeCss;
//                     const renderMenuItems = (childMenuItems: MappedLink[], currentDepth: number) => {
//                         const cssOverrides = spreadProps("cssOverrides");
//                         const maxDepth = otherProps.maxDepth || 3;
//                         return (
//                             <MenuStyle
//                                 width={otherProps.width}
//                                 css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
//                             >
//                                 {childMenuItems
//                                     ?.filter(item => !item.excludeFromNavigation)
//                                     .map((item, index) => {
//                                         const hasChildren =
//                                             item.children?.some(o => !o.excludeFromNavigation) &&
//                                             currentDepth < maxDepth;

//                                         if (
//                                             (item.type === "ChangeCustomerPage" &&
//                                                 !this.props.displayChangeCustomerLink) ||
//                                             !this.state.shouldRender
//                                         ) {
//                                             return null;
//                                         }

//                                         return (
//                                             <MenuItemStyle
//                                                 width={otherProps.width}
//                                                 css={applyStyledProp(["cssOverrides", "menuItem"], resolvedMergeCss)}
//                                                 key={`${item.title}_${index}`}
//                                                 data-test-selector="menuItem"
//                                                 onClick={() => {
//                                                     this.closeMenuWithoutChildren(hasChildren);
//                                                 }}
//                                             >
//                                                 <Clickable
//                                                     href={item.url}
//                                                     onClick={this.closeMenu}
//                                                     target={item.openInNewWindow ? "_blank" : ""}
//                                                     {...applyProp("menuItemClickableProps")}
//                                                     {...item.clickableProps}
//                                                 >
//                                                     {item.iconProps && <IconMemo {...item.iconProps} />}
//                                                     <MenuItemText
//                                                         hasChildren={hasChildren}
//                                                         {...applyProp("menuItemTypographyProps")}
//                                                         data-test-selector="menuItemText"
//                                                     >
//                                                         {item.title}
//                                                     </MenuItemText>
//                                                     {hasChildren && <IconMemo {...applyProp("moreIconProps")} />}
//                                                 </Clickable>
//                                                 {hasChildren
//                                                     ? renderMenuItems(item!.children!, currentDepth + 1)
//                                                     : null}
//                                             </MenuItemStyle>
//                                         );
//                                     })}
//                             </MenuStyle>
//                         );
//                     };

//                     const cssOverrides = spreadProps("cssOverrides");
//                     const descriptionId = otherProps.descriptionId || uniqueId();
//                     return (
//                         <>
//                             <MenuWrapper
//                                 ref={this.menuWrapperRef}
//                                 canBeOpen={this.state.canBeOpen}
//                                 isOpen={isOpen}
//                                 onMouseLeave={this.menuWrapperMouseLeaveHandler}
//                                 css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
//                                 {...{ "aria-labelledby": descriptionId }}
//                                 {...otherProps}
//                             >
//                                 {React.cloneElement(menuTrigger, { id: descriptionId, className: "trigger" })}
//                                 {renderMenuItems(menuItems, 1)}
//                             </MenuWrapper>
//                             <span ref={trigger} tabIndex={-1} />
//                         </>
//                     );
//                 }}
//             </ThemeConsumer>
//         );
//     }
// }

// /** @component */
// export default withTheme(Menu);

// export { MenuItemText };



